import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { FileInterface } from 'src/model/file.interface';

export interface Download {
  file: string;
  filename: string;
}

export type UrlBuilder = (name: string) => string;

@Injectable({providedIn: 'root'})
export class FileUrlBuilder {
  private constructApi = inject(ConstructAPI);

  private encodeFilename(path: string) {
    return path.replace(/([^/]+)$/, (match) => encodeURIComponent(match));
  }

  buildCustomerFileUrl(customerId: number): UrlBuilder {
    return (name: string) => this.encodeFilename(this.constructApi.getFullFile(customerId, name));
  }

  buildSetFileUrl(setId: number): UrlBuilder {
    return (name: string) => this.encodeFilename(this.constructApi.getSetFileFull(setId, name));
  }

  buildBlacklistFileUrl(blacklistId: number): UrlBuilder {
    return (name: string) => this.encodeFilename(this.constructApi.getBlacklistFileFull(blacklistId, name));
  }

  buildReserveGroupFile(reserveGroupId: number): UrlBuilder {
    return (name: string) => this.encodeFilename(this.constructApi.getReserveGroupFileFull(reserveGroupId, name));
  }
}

@Injectable({
  providedIn: 'root'
})
export class FileAdapter {
  private sharedService = inject(SharedService);

  adapt(item: any, buildUrl?: UrlBuilder): FileInterface {
    return {
      label: item.Name,
      data: item.Data,
      contentType: item['Content-Type'],
      lastModified: item['Last-Modified'],
      uploadedBy: item['Uploaded-By'],
      fileType: this.sharedService.checkFileType(item['Content-Type'], item.Name),
      url: buildUrl ? buildUrl(item.Name) : ''
    } as FileInterface;
  }
}

@Injectable({
  providedIn: 'root'
})
export class FileService {
  private api = inject(RestApiService);
  private adapter = inject(FileAdapter);

  getFiles(url: string): Promise<FileInterface[]> {
    return new Promise<FileInterface[]>((resolve, reject) => {
      this.api.get<FileInterface[]>(url).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
          next: (data) => {
              resolve(data);
          },
          error: (error) => {
              this.api.handleError(error);
              reject(error);
          }
      })
    });
  }

  downloadFile(d: Download) {
    const a = document.createElement('a');
    a.href = d.file;
    a.download = d.filename;

    // Append to the body (required for Firefox compatibility)
    document.body.appendChild(a);
    a.click(); // Trigger the download

    // Clean up: remove the link and revoke the Blob URL
    document.body.removeChild(a);
    URL.revokeObjectURL(d.file);
  }

  getFilenameFromContentDisposition(content: string): string {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(content);

    if (matches != null && matches[1]) {
      // matches[1] is the first caturing group which is regex ((['"]).*?\2|[^;\n]*)
      // Remove any surrounding quotes from the filename
      const filename = matches[1].replace(/['"]/g, '');
      return filename;
    }

    return 'defaultFilename';
  }

  getFileByUrl(url: string, filename?: string): Promise<Download> {
    return new Promise<Download>((resolve, reject) => {
      this.api.getFile(url).subscribe({
        next: response => {
          const blob = new Blob([response.body as BlobPart], { type: response.headers.get('Content-Type') });
          const url = window.URL.createObjectURL(blob);
          const result: Download = { file: url, filename: filename ?? this.getFilenameFromContentDisposition(response.headers.get('content-disposition')) }
          resolve(result);
        },
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  createFile(res: HttpResponse<any>): Download {
    const filename = this.getFilenameFromContentDisposition(res.headers.get('content-disposition'));
    const blob = new Blob([res.body as BlobPart], { type: res.headers.get('Content-Type') });
    const url = window.URL.createObjectURL(blob);
    return { file: url, filename: filename };
  }

  deleteFile(url: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.api.delete(url).subscribe({
        next: () => resolve(true),
        error: (error) => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }
}
